import { FormEvent, ReactNode, Ref } from 'react';

import Button from 'components/ui/button';
import { FOCO_VISIVEL } from 'utils/foco';
import Paginacao from 'components/ui/paginacao';
import { iniciaisDoFunci } from 'pages/Rede/feed-calculos';

import { PersonAvatar } from 'components/person-avatar';

import { LinhaDaEquipe, numeroPtBR } from '../minha-equipe-calculos';
import PilulaDeStatus from './PilulaDeStatus';

export const POR_PAGINA_DA_EQUIPE = 10;

export type EstadoDaExportacao = 'ocioso' | 'gerando' | 'gerado' | 'erro';

export interface ListaDaEquipeProps {
  linhas: LinhaDaEquipe[];
  /** `range.total` do `equipe_gestor`. */
  total: number;
  /** Página atual, a partir de 0. */
  pagina: number;
  busca: string;
  /** `equipe_gestor` carregando. */
  carregando: boolean;
  /** Resumo da equipe carregando (métricas e status das linhas). */
  carregandoNumeros: boolean;
  erroDaLista: boolean;
  /** Resumo falhou: as linhas chegam com métricas `null`. */
  erroDoResumo: boolean;
  /** Gestor lateral em exibição, ou null na própria equipe. */
  equipeLateral: { nomeGuerra: string } | null;
  exportacao: EstadoDaExportacao;
  onBuscaChange: (termo: string) => void;
  /** Enter na busca: buscar sem esperar. */
  onBuscaSubmit: (termo: string) => void;
  onPagina: (pagina: number) => void;
  onVerMais: (linha: LinhaDaEquipe) => void;
  onTentarDeNovo: () => void;
  /** Só no modo lateral: volta à própria equipe. Entrar numa equipe lateral é pelo cartão do panorama (MEQ2-06). */
  onVoltarParaMinhaEquipe: () => void;
  /** Título da lista: recebe o foco ao entrar e ao sair da equipe lateral (MEQ2-04). */
  refDoTitulo?: Ref<HTMLHeadingElement>;
  onExportar: () => void;
  onAdicionar: () => void;
}

const SUBTITULO_DA_EQUIPE =
  'Realizações cadastradas, selos e reconhecimentos de cada liderado no ciclo, e o status da realização na fase.';
const SUBTITULO_LATERAL =
  'Equipes que você aprecia em lateralidade ou substituição. A pontuação dessas apreciações não é creditada a você.';

const CORES_DAS_INICIAIS = ['bg-brand-green', 'bg-brand-purple-light', 'bg-brand-pink-light', 'bg-brand-yellow-light'];

// min-h-[24px]: alvo de 24px (WCAG 2.5.8; o IBM acusou o espaçamento entre as ações de texto).
const ACAO_DE_TEXTO = `inline-flex min-h-[24px] cursor-pointer items-center gap-[7px] whitespace-nowrap border-0 bg-transparent p-0 font-text text-[10.5px] font-bold uppercase tracking-[0.04em] text-brand-blue hover:text-brand-blue-dark disabled:cursor-not-allowed disabled:opacity-60 ${FOCO_VISIVEL}`;

const icone = (caminhos: ReactNode, tamanho = 14) => (
  <svg
    width={tamanho}
    height={tamanho}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {caminhos}
  </svg>
);

const ICONE_EQUIPE = icone(
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </>,
);
const ICONE_EXPORTAR = icone(
  <>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="m7 10 5 5 5-5" />
    <path d="M12 15V3" />
  </>,
);
const ICONE_ADICIONAR = icone(
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M19 8v6M22 11h-6" />
  </>,
);

function rotuloDaExportacao(estado: EstadoDaExportacao): string {
  if (estado === 'gerando') return 'Gerando…';
  if (estado === 'gerado') return 'Arquivo gerado';
  return 'Exportar para Excel';
}

function Carregando({ largura }: { largura: string }) {
  return (
    <span
      className={`block h-4 animate-pulse rounded-jj-sm bg-surface-200 motion-reduce:animate-none ${largura}`}
      aria-hidden="true"
    />
  );
}

function Metrica({ rotulo, valor, carregando }: { rotulo: string; valor: number | null; carregando: boolean }) {
  return (
    <div>
      <dt className="text-[9.5px] font-normal uppercase leading-[normal] tracking-[0.05em] text-content-soft">{rotulo}</dt>
      <dd className="m-0 font-display text-[15px] font-bold leading-[normal] text-brand-blue">
        {carregando ? <Carregando largura="w-10" /> : numeroPtBR(valor)}
      </dd>
    </div>
  );
}

function LinhaDoLiderado({
  linha,
  posicao,
  carregandoNumeros,
  onVerMais,
}: {
  linha: LinhaDaEquipe;
  posicao: number;
  carregandoNumeros: boolean;
  onVerMais: (linha: LinhaDaEquipe) => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-3.5 rounded-jj-lg border border-solid border-line px-4 py-3.5 transition hover:border-brand-blue hover:shadow-[0_6px_16px_rgba(0,45,75,0.10)]">
      <PersonAvatar
        image={linha.rosto.image}
        avatar={linha.rosto.avatar}
        avatarType={linha.rosto.avatarType}
        initials={iniciaisDoFunci(linha.nome)}
        size={40}
        initialsClassName={`font-display text-[13px] font-bold text-brand-navy ${CORES_DAS_INICIAIS[posicao % CORES_DAS_INICIAIS.length]}`}
      />
      <div className="min-w-0 flex-[1_1_190px]">
        <p className="m-0 text-[13.5px] font-bold text-content">
          {linha.nome} <span className="font-normal text-content-soft">({linha.chave})</span>
        </p>
        <p className="m-0 mt-[3px] text-[11px] text-content-soft">
          {linha.diretoria} · {linha.funcao}
        </p>
      </div>
      <dl className="m-0 flex flex-wrap gap-5">
        <Metrica rotulo="Realizações cadastradas" valor={linha.realizacoes} carregando={carregandoNumeros} />
        <Metrica rotulo="Selos" valor={linha.selos} carregando={carregandoNumeros} />
        <Metrica rotulo="Reconhecimentos" valor={linha.reconhecimentos} carregando={carregandoNumeros} />
      </dl>
      {carregandoNumeros ? (
        <Carregando largura="w-32" />
      ) : (
        <PilulaDeStatus status={linha.status} rotuloSemStatus="—" className="px-3 py-[5px] text-[9.5px]" />
      )}
      <button
        type="button"
        onClick={() => onVerMais(linha)}
        className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-jj-field border border-solid border-brand-blue bg-transparent px-[13px] py-2 font-text text-[10px] font-bold uppercase tracking-[0.04em] text-brand-blue hover:bg-brand-blue hover:text-brand-white ${FOCO_VISIVEL}`}
      >
        Ver mais<span className="sr-only"> sobre {linha.nome}</span>
        {icone(<path d="m9 18 6-6-6-6" />, 13)}
      </button>
    </li>
  );
}

function Aviso({ titulo, texto, children }: { titulo: string; texto?: string; children?: ReactNode }) {
  return (
    <div className="rounded-jj-lg border border-dashed border-surface-300 px-7 py-[26px]">
      <p className="m-0 mb-1.5 font-display text-[15px] font-bold text-brand-blue-dark">{titulo}</p>
      {texto && <p className="m-0 text-[12.5px] leading-[1.6] text-content-soft [text-wrap:pretty]">{texto}</p>}
      {children}
    </div>
  );
}

/** Lista paginada dos liderados (ou da equipe lateral) com busca, métricas do ciclo e status na fase. */
export default function ListaDaEquipe({
  linhas,
  total,
  pagina,
  busca,
  carregando,
  carregandoNumeros,
  erroDaLista,
  erroDoResumo,
  equipeLateral,
  exportacao,
  onBuscaChange,
  onBuscaSubmit,
  onPagina,
  onVerMais,
  onTentarDeNovo,
  onVoltarParaMinhaEquipe,
  onExportar,
  onAdicionar,
  refDoTitulo,
}: ListaDaEquipeProps) {
  const ultimaPagina = Math.max(0, Math.ceil(total / POR_PAGINA_DA_EQUIPE) - 1);
  const inicio = pagina * POR_PAGINA_DA_EQUIPE;
  const vazia = !carregando && !erroDaLista && linhas.length === 0;
  const ocupada = carregando || carregandoNumeros;

  function buscar(evento: FormEvent) {
    evento.preventDefault();
    onBuscaSubmit(busca);
  }

  return (
    <section aria-labelledby="titulo-lista" className="mt-[22px] rounded-jj-lg bg-brand-white px-[26px] py-6 shadow-jj-01">
      <div className="flex flex-wrap items-center gap-[11px]">
        <h2
          id="titulo-lista"
          ref={refDoTitulo}
          tabIndex={-1}
          className="m-0 scroll-mt-6 font-display text-xl font-bold leading-[normal] text-brand-blue-dark focus:outline-none"
        >
          {equipeLateral ? `Equipe de ${equipeLateral.nomeGuerra}` : 'Funcionários da sua equipe'}
        </h2>
        {/* SPEC_DEVIATION: fundo `brand-blue-dark` (amarelo pequeno sobre `brand-blue` dá 4,44:1, EQP-33). */}
        <span className="rounded-jj-pill bg-brand-blue-dark px-[11px] py-1 font-display text-[11px] font-bold text-brand-yellow">
          {total}
          <span className="sr-only"> {total === 1 ? 'funcionário' : 'funcionários'}</span>
        </span>
        <div className="ml-auto flex flex-wrap items-center gap-4">
          {equipeLateral && (
            <button type="button" onClick={onVoltarParaMinhaEquipe} className={ACAO_DE_TEXTO}>
              {ICONE_EQUIPE}
              Voltar para minha equipe
            </button>
          )}
          <button type="button" onClick={onExportar} disabled={exportacao === 'gerando'} className={ACAO_DE_TEXTO}>
            {ICONE_EXPORTAR}
            <span aria-live="polite">{rotuloDaExportacao(exportacao)}</span>
          </button>
          {!equipeLateral && (
            <Button
              variant="solido-destaque"
              size="sm"
              onClick={onAdicionar}
              className="gap-[7px] !py-2.5 !text-[10.5px] !leading-[normal]"
            >
              {ICONE_ADICIONAR}
              Adicionar funcionário
            </Button>
          )}
        </div>
      </div>
      <p className="m-0 mb-4 mt-2 text-xs leading-[normal] text-content-soft [text-wrap:pretty]">
        {equipeLateral ? SUBTITULO_LATERAL : SUBTITULO_DA_EQUIPE}
      </p>

      {exportacao === 'erro' && (
        <p role="alert" className="m-0 mb-4 text-xs font-bold text-urgent">
          Não foi possível gerar o arquivo.
        </p>
      )}

      <form role="search" onSubmit={buscar} className="relative mb-4">
        {/* SPEC_DEVIATION: borda `content-soft` (design #E5E7EB, 1,24:1) para o limite do campo passar 3:1, como no Feed. */}
        <input
          type="search"
          value={busca}
          onChange={evento => onBuscaChange(evento.target.value)}
          placeholder="Pesquise com nome e chave"
          aria-label="Pesquisar funcionário por nome ou chave"
          className="box-border w-full rounded-jj-field border border-solid border-content-soft bg-brand-white py-[11px] pl-3.5 pr-10 font-text text-[13px] text-content outline-none focus:border-brand-blue focus:shadow-[0_0_0_3px_rgba(70,94,255,0.14)]"
        />
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--brand-blue)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-5-5" />
        </svg>
      </form>

      {erroDoResumo && !erroDaLista && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center gap-3 rounded-jj-md bg-brand-yellow-light px-4 py-3 text-xs text-content"
        >
          Não foi possível carregar os números da equipe.
          <Button variant="solido-destaque" size="sm" onClick={onTentarDeNovo}>
            Tentar de novo
          </Button>
        </div>
      )}

      {erroDaLista ? (
        <Aviso titulo="Não foi possível carregar sua equipe.">
          <Button variant="solido-destaque" size="sm" onClick={onTentarDeNovo} className="mt-3">
            Tentar de novo
          </Button>
        </Aviso>
      ) : vazia ? (
        <Aviso
          titulo="Nenhum funcionário encontrado"
          texto="Revise o nome ou a chave pesquisada. Se o funcionário passou a ser seu liderado depois do fechamento do planejamento, use Adicionar funcionário."
        />
      ) : (
        <ul aria-labelledby="titulo-lista" aria-busy={ocupada} className="m-0 flex list-none flex-col gap-2.5 p-0">
          {carregando
            ? Array.from({ length: 3 }, (_, indice) => (
                <li
                  key={indice}
                  aria-hidden="true"
                  className="flex items-center gap-3.5 rounded-jj-lg border border-solid border-surface-200 px-4 py-3.5"
                >
                  <span className="h-10 w-10 shrink-0 animate-pulse rounded-full bg-surface-200 motion-reduce:animate-none" />
                  <Carregando largura="w-1/3" />
                </li>
              ))
            : linhas.map((linha, indice) => (
                <LinhaDoLiderado
                  key={linha.chave}
                  linha={linha}
                  posicao={inicio + indice}
                  carregandoNumeros={carregandoNumeros}
                  onVerMais={onVerMais}
                />
              ))}
        </ul>
      )}

      {!erroDaLista && total > POR_PAGINA_DA_EQUIPE && (
        <div className="mt-[18px] flex flex-wrap items-center gap-2 border-t border-surface-200 pt-4">
          <span className="text-[11px] text-content-soft">
            {inicio + 1}–{Math.min(inicio + POR_PAGINA_DA_EQUIPE, total)} de {total}
          </span>
          <Paginacao
            total={ultimaPagina + 1}
            atual={pagina}
            onChange={onPagina}
            rotulo="Paginação de funcionários"
            className="ml-auto"
          />
        </div>
      )}
    </section>
  );
}
